/**
 * DesignBriefDisplay (v1.8.0) — read-only render of a ContentPost's
 * `designAssetBrief`. Falls back to `BriefEmptyState` when the brief is
 * absent (legacy posts) and lets the user trigger regeneration through
 * `BriefRegenerateActions`.
 *
 * Mounted in `UnifiedPostDialog` ABOVE `AiAssetsEditor`. The brief is
 * never mutated in-place — the user uses the regen buttons to dispatch
 * the backend chain.
 */

import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';

import type { DesignAssetBrief } from '@segmently/schema-models/content-plan';
import { BriefEmptyState } from './BriefEmptyState';
import { BriefStatusBadge } from './BriefStatusBadge';
import { BriefRegenerateActions } from './BriefRegenerateActions';
import { StoryboardCardList } from './StoryboardCardList';
import { AcceptanceCriteriaList } from './AcceptanceCriteriaList';

export interface DesignBriefDisplayProps {
  /** Brief from `post.designAssetBrief`. Absent → empty state CTA. */
  brief?: DesignAssetBrief;
  /** Backend callbacks dispatching the regen endpoints. */
  onRegenerateBrief: () => Promise<void>;
  onRegeneratePost?: () => Promise<void>;
  /** Loading flag to disable buttons during in-flight requests. */
  busy?: boolean;
  /** When true the post is a master and regen-post will mark derivatives stale. */
  isMaster?: boolean;
}

export const DesignBriefDisplay: React.FC<DesignBriefDisplayProps> = ({
  brief,
  onRegenerateBrief,
  onRegeneratePost,
  busy = false,
  isMaster = false,
}) => {
  const hasBrief = Boolean(brief && brief.status && brief.status !== 'pending');

  const summary = useMemo(() => {
    if (!brief) return null;
    return {
      function: brief.designFunction?.primary ?? '—',
      mechanism: brief.visualStrategy?.primaryMechanism ?? '—',
      cards: brief.storyboard?.length ?? 0,
      mainIdea: brief.message?.mainIdea ?? '',
    };
  }, [brief]);

  if (!hasBrief || !brief) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }} data-testid="design-brief-section">
        <BriefEmptyState onGenerate={onRegenerateBrief} busy={busy} />
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="design-brief-section">
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6" data-testid="design-brief-title">
              Design asset brief
            </Typography>
            <BriefStatusBadge status={brief.status} />
          </Stack>
          <BriefRegenerateActions
            onRegenerateBrief={onRegenerateBrief}
            onRegeneratePost={onRegeneratePost}
            busy={busy}
            isMaster={isMaster}
          />
        </Stack>

        {summary && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              Function · Mechanism · Cards
            </Typography>
            <Typography variant="body2">
              <strong>{summary.function}</strong> · {summary.mechanism} · {summary.cards} card{summary.cards === 1 ? '' : 's'}
            </Typography>
            {summary.mainIdea && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {summary.mainIdea}
              </Typography>
            )}
          </Box>
        )}

        <Divider />

        {brief.storyboard && brief.storyboard.length > 0 && (
          <StoryboardCardList cards={brief.storyboard} />
        )}

        {brief.acceptanceCriteria && brief.acceptanceCriteria.length > 0 && (
          <>
            <Divider />
            <AcceptanceCriteriaList criteria={brief.acceptanceCriteria} />
          </>
        )}

        {brief.failureReason && (
          <Box
            sx={{ p: 1, borderRadius: 1, bgcolor: 'error.lighter', color: 'error.darker' }}
            data-testid="design-brief-failure-reason"
          >
            <Typography variant="caption">
              Acceptance failure: {brief.failureReason}
            </Typography>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};
